import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePageTraffic } from "@/lib/usePageTraffic";
import { toast } from "sonner";
import {
  ScanLine, FileText, Loader2, X, Search, CheckCircle2, AlertTriangle,
  ChevronRight, ChevronDown, Download, UserCheck, FileSearch,
} from "lucide-react";
import { IconRefresh } from "@/lib/icons";
import { useClient } from "@/lib/clientContext";
import {
  uploadDocument, getJob, listAnalyses, getAnalysis, getFileContent, assignPatient,
  searchPatients, fileToBase64, MAX_FILE_BYTES, ACCEPTED, STAGE_STEPS, engineTag, genderCode, summarizeExtraction,
} from "@/lib/luminaDocs";

const INK = "#141412", INDIGO = "#5856D6", HAIR = "#E9E9EC", GREY = "#6E6E6B", SUB = "#6C6C69", SOFT = "#F6F6F8";
const PAGE = 30;

const CHIPS = [
  { k: "", t: "All" },
  { k: "auto_linked", t: "Auto-linked" },
  { k: "needs_review", t: "Needs review" },
  { k: "confirmed", t: "Confirmed" },
];

const LINK_META = {
  auto_linked: { t: "Auto-linked", fg: "#0F7A42", bg: "#E7F6EC" },
  needs_review: { t: "Needs review", fg: "#9A5A00", bg: "#FDF3E3" },
  confirmed: { t: "Confirmed", fg: "#3A3AA0", bg: "#ECECFB" },
};
const MATCH_META = {
  matched: { t: "Matched", fg: "#0F7A42", bg: "#E7F6EC" },
  tentative: { t: "Tentative", fg: "#9A5A00", bg: "#FDF3E3" },
  no_match: { t: "No match", fg: "#C4302A", bg: "#FBECEA" },
};

function EngineTag({ engine }) {
  if (!engine) return null;
  const tag = engineTag(engine);
  return (
    <span title={`AI engine: ${engine}`} data-testid="lumina-engine-tag"
      className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-[5px] text-[10px] font-bold"
      style={{ background: tag === "A" ? "#EDE7FB" : "#E6F0FB", color: tag === "A" ? "#5B3FBF" : "#2A6BBF" }}>
      {tag}
    </span>
  );
}

function Pill({ meta, testid }) {
  if (!meta) return null;
  return <span data-testid={testid} className="inline-flex items-center text-[10.5px] font-bold uppercase tracking-[.03em] rounded-full px-2 py-[2px]" style={{ color: meta.fg, background: meta.bg }}>{meta.t}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
const prettyType = (t) => String(t || "document").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* ---------------- Upload dropzone + job tracker ---------------- */
function StageTracker({ stage, status, error }) {
  if (status === "failed") return <span className="text-[11px] font-semibold" style={{ color: "#C4302A" }}>Failed — {error || "processing error"}</span>;
  if (status === "completed") return <span className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: "#0F7A42" }}><CheckCircle2 size={13} /> Done</span>;
  const idx = Math.max(0, STAGE_STEPS.findIndex((s) => s.key === stage));
  return (
    <span className="flex items-center gap-1.5" data-testid="lumina-stage">
      {STAGE_STEPS.map((s, i) => (
        <span key={s.key} className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: i <= idx ? INDIGO : "#D6D6DA" }} />
          <span className="text-[10.5px]" style={{ color: i === idx ? INK : GREY, fontWeight: i === idx ? 700 : 500 }}>{s.label}</span>
          {i < STAGE_STEPS.length - 1 ? <span className="w-3 h-px" style={{ background: "#E2E2E6" }} /> : null}
        </span>
      ))}
    </span>
  );
}

/* ---------------- Detail drawer ---------------- */
const jsonLabel = (k) => String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const isEmptyVal = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0) || (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

// Render any API value (nested objects/arrays included) in a human-readable layout
// instead of raw JSON. e.g. a "review_of_systems" object becomes labelled sub-rows.
function prettyJson(value, depth = 0) {
  if (isEmptyVal(value)) return <span style={{ color: GREY }}>—</span>;
  if (typeof value !== "object") return <span>{String(value)}</span>;
  if (Array.isArray(value)) {
    const allPrimitive = value.every((x) => x == null || typeof x !== "object");
    if (allPrimitive) return <span>{value.filter((x) => x != null && x !== "").join(", ")}</span>;
    return (
      <div className="space-y-1.5">
        {value.map((item, i) => (
          <div key={i} className="rounded-[8px] border px-2.5 py-1.5" style={{ borderColor: "#EEEEF0" }}>{prettyJson(item, depth + 1)}</div>
        ))}
      </div>
    );
  }
  const entries = Object.entries(value).filter(([, v]) => !isEmptyVal(v));
  if (!entries.length) return <span style={{ color: GREY }}>—</span>;
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => {
        const nested = v && typeof v === "object";
        return (
          <div key={k} className={nested ? "" : "flex gap-2"}>
            <span className="text-[11.5px] font-semibold shrink-0" style={{ color: SUB }}>{jsonLabel(k)}{nested ? "" : ":"}</span>
            <div className={nested ? "mt-0.5 ml-2 pl-2.5 border-l" : "text-[12.5px] break-words min-w-0"} style={nested ? { borderColor: "#ECECEE" } : { color: INK }}>
              {prettyJson(v, depth + 1)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KV({ k, v }) {
  const empty = v == null || v === "";
  return (
    <div className="flex gap-3 py-1.5 border-b last:border-0" style={{ borderColor: "#F0F0F2" }}>
      <div className="text-[11px] font-semibold uppercase tracking-[.02em] shrink-0 w-[150px]" style={{ color: GREY }}>{prettyType(k)}</div>
      <div className="text-[12.5px] min-w-0 break-words" style={{ color: INK }}>
        {empty ? "—" : (typeof v === "object" ? prettyJson(v) : String(v))}
      </div>
    </div>
  );
}

function PatientAssign({ clientId, analysisId, match, onAssigned }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState("");

  const run = async () => {
    if (!q.trim() || !clientId) return;
    setSearching(true);
    try { const d = await searchPatients({ client_id: clientId, q: q.trim() }); setResults(d.results || []); }
    catch { toast.error("Patient search failed."); setResults([]); }
    finally { setSearching(false); }
  };

  const assign = async (r) => {
    setAssigning(r.chart_id || r.mrn || r.full_name);
    try {
      await assignPatient(analysisId, {
        chart_id: r.chart_id || null, chart_uuid: r.chart_uuid || null, mrn: r.mrn || null,
        full_name: r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim(),
        dob: r.dob || null, gender: genderCode(r.gender), source: r.source || "postgres",
      });
      toast.success("Patient confirmed");
      onAssigned?.();
    } catch { toast.error("Could not assign the patient."); }
    finally { setAssigning(""); }
  };

  const cur = match?.patient;
  const alts = Array.isArray(match?.alternatives) ? match.alternatives : [];

  return (
    <div className="mt-3">
      {cur ? (
        <div className="rounded-[10px] border p-3 mb-2" style={{ borderColor: HAIR, background: "#FBFBFD" }} data-testid="lumina-current-match">
          <div className="text-[11px] font-semibold uppercase tracking-[.02em] mb-1" style={{ color: GREY }}>Current match{match?.confidence != null ? ` · ${(match.confidence * 100).toFixed(0)}%` : ""}</div>
          <div className="text-[13px] font-semibold" style={{ color: INK }}>{cur.full_name || "—"}</div>
          <div className="text-[11.5px]" style={{ color: SUB }}>MRN {cur.mrn || "—"} · DOB {cur.dob || "—"} · {cur.gender || "—"}</div>
        </div>
      ) : null}

      {alts.length ? (
        <div className="mb-2">
          <div className="text-[11px] font-semibold uppercase tracking-[.02em] mb-1" style={{ color: GREY }}>Other candidates</div>
          {alts.map((a, i) => (
            <button key={i} onClick={() => assign(a)} disabled={!!assigning} data-testid={`lumina-alt-${i}`}
              className="w-full text-left flex items-center gap-2 rounded-[8px] border px-3 py-2 mb-1 hover:bg-black/[.02] disabled:opacity-50" style={{ borderColor: HAIR }}>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold truncate" style={{ color: INK }}>{a.full_name || "—"}</div>
                <div className="text-[11px]" style={{ color: SUB }}>MRN {a.mrn || "—"} · DOB {a.dob || "—"}</div>
              </div>
              {assigning === (a.chart_id || a.mrn || a.full_name) ? <Loader2 size={13} className="animate-spin" /> : <span className="text-[11px] font-bold" style={{ color: INDIGO }}>Assign</span>}
            </button>
          ))}
        </div>
      ) : null}

      <div className="text-[11px] font-semibold uppercase tracking-[.02em] mb-1 mt-3" style={{ color: GREY }}>Search &amp; confirm patient · client {clientId}</div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 flex-1 border rounded-[8px] px-3 py-2 bg-white" style={{ borderColor: HAIR }}>
          <Search size={14} color={GREY} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Name, DOB or MRN" className="flex-1 text-[12.5px] outline-none bg-transparent" data-testid="lumina-patient-search-input" />
        </div>
        <button onClick={run} disabled={searching || !q.trim()} className="text-[12px] font-bold text-white rounded-[8px] px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: INK }} data-testid="lumina-patient-search-btn">
          {searching ? <Loader2 size={12} className="animate-spin" /> : null} Search
        </button>
      </div>
      {results != null ? (
        <div className="mt-2" data-testid="lumina-search-results">
          {results.length === 0 ? <div className="text-[12px] py-2" style={{ color: GREY }}>No patients found in this client.</div> : null}
          {results.map((r, i) => (
            <button key={i} onClick={() => assign(r)} disabled={!!assigning} data-testid={`lumina-result-${i}`}
              className="w-full text-left flex items-center gap-2 rounded-[8px] border px-3 py-2 mb-1 hover:bg-black/[.02] disabled:opacity-50" style={{ borderColor: HAIR }}>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold truncate" style={{ color: INK }}>{r.full_name || `${r.first_name || ""} ${r.last_name || ""}`}</div>
                <div className="text-[11px]" style={{ color: SUB }}>MRN {r.mrn || "—"} · DOB {r.dob || "—"} · {r.source || ""}</div>
              </div>
              {assigning === (r.chart_id || r.mrn || r.full_name) ? <Loader2 size={13} className="animate-spin" /> : <span className="text-[11px] font-bold" style={{ color: INDIGO }}>Assign</span>}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DetailDrawer({ clientId, analysisId, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await getAnalysis(analysisId); setA(d); } catch { toast.error("Could not load the document."); }
    finally { setLoading(false); }
  }, [analysisId]);

  useEffect(() => { load(); }, [load]);

  const docId = a?.document_id;
  const loadFile = async () => {
    if (!docId) return;
    setFileLoading(true);
    try { const d = await getFileContent(docId); setFile(d); } catch { toast.error("Could not load the original file."); }
    finally { setFileLoading(false); }
  };
  useEffect(() => { if (docId) loadFile(); /* eslint-disable-next-line */ }, [docId]);

  const classification = a?.classification || a?.result?.classification || {};
  const extraction = a?.extraction || a?.result?.extraction || {};
  const match = a?.edi_client_match || a?.result?.edi_client_match || {};
  const engine = a?.ai_engine || a?.result?.ai_engine;
  const linkStatus = a?.edi_link_status;
  const dataUrl = file?.file_content ? `data:${file.content_type || "application/octet-stream"};base64,${file.file_content}` : null;
  const isPdf = (file?.content_type || "").includes("pdf");

  return (
    <div className="fixed inset-0 z-[70]" data-testid="lumina-detail-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-full max-w-[640px] bg-white shadow-2xl flex flex-col">
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: HAIR }}>
          <FileText size={18} color={INDIGO} />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-bold truncate" style={{ color: INK }}>{prettyType(classification.document_type)}</div>
            <div className="text-[11.5px]" style={{ color: SUB }}>{file?.filename || a?.filename || "Document"}</div>
          </div>
          <EngineTag engine={engine} />
          <Pill meta={LINK_META[linkStatus]} testid="lumina-detail-link" />
          <button onClick={onClose} data-testid="lumina-detail-close" className="p-1.5 rounded-[8px] hover:bg-black/[.05]"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" color={GREY} /></div>
          ) : (
            <>
              {/* Match + assign */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck size={15} color={INK} />
                  <span className="text-[13px] font-bold" style={{ color: INK }}>Patient match</span>
                  <Pill meta={MATCH_META[match?.status]} testid="lumina-detail-match" />
                </div>
                <PatientAssign clientId={clientId} analysisId={analysisId} match={match} onAssigned={() => { load(); onChanged?.(); }} />
              </div>

              {/* Extraction */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2"><FileSearch size={15} color={INK} /><span className="text-[13px] font-bold" style={{ color: INK }}>Extracted fields</span></div>
                <div className="rounded-[10px] border p-3" style={{ borderColor: HAIR }}>
                  {extraction && Object.keys(extraction).length ? Object.entries(extraction).map(([k, v]) => <KV key={k} k={k} v={v} />) : <div className="text-[12px]" style={{ color: GREY }}>No fields extracted.</div>}
                </div>
              </div>

              {/* Original file */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={15} color={INK} /><span className="text-[13px] font-bold" style={{ color: INK }}>Original file</span>
                  {dataUrl ? <a href={dataUrl} download={file?.filename || "document"} className="ml-auto text-[11.5px] font-bold inline-flex items-center gap-1" style={{ color: INDIGO }} data-testid="lumina-download"><Download size={13} /> Download</a> : null}
                </div>
                {fileLoading ? <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" color={GREY} /></div> : null}
                {dataUrl && !fileLoading ? (
                  isPdf
                    ? <iframe title="document" src={dataUrl} className="w-full rounded-[10px] border" style={{ height: 460, borderColor: HAIR }} data-testid="lumina-file-pdf" />
                    : <img alt="document" src={dataUrl} className="w-full rounded-[10px] border" style={{ borderColor: HAIR }} data-testid="lumina-file-img" />
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
function DocRow({ row, onOpen }) {
  const m = row.edi_client_match || {};
  const summary = summarizeExtraction(row.extraction, 4);
  return (
    <button onClick={() => onOpen(row.id)} data-testid={`lumina-row-${row.id}`}
      className="w-full text-left flex items-center gap-3 px-4 py-3 border-b last:border-0 hover:bg-black/[.015]" style={{ borderColor: "#F0F0F2" }}>
      <FileText size={16} color={GREY} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold truncate" style={{ color: INK }}>{prettyType(row.classification?.document_type)}</span>
          <EngineTag engine={row.ai_engine} />
        </div>
        <div className="text-[11.5px] mt-0.5 truncate" style={{ color: SUB }}>
          {fmtDate(row.created_at)}
          {m.patient?.full_name ? ` · ${m.patient.full_name}` : ""}
          {m.confidence != null ? ` · ${(m.confidence * 100).toFixed(0)}%` : ""}
        </div>
        {summary.length ? (
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`lumina-row-fields-${row.id}`}>
            {summary.map((f, i) => (
              <span key={i} className="inline-flex items-baseline gap-1 text-[11px] rounded-[6px] px-1.5 py-[2px]" style={{ background: "#F3F3F6" }}>
                <span className="font-semibold" style={{ color: GREY }}>{f.label}</span>
                <span className="truncate max-w-[180px]" style={{ color: INK }}>{f.value}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <Pill meta={MATCH_META[m.status]} testid={`lumina-row-match-${row.id}`} />
      <Pill meta={LINK_META[row.edi_link_status]} testid={`lumina-row-link-${row.id}`} />
      <ChevronRight size={15} color={GREY} className="shrink-0" />
    </button>
  );
}

export default function LuminaDocuments() {
  const { selectedClientId } = useClient();
  const [allRows, setAllRows] = useState([]);
  const { report: reportTraffic } = usePageTraffic("lumina-documents");
  const ldFirst = useRef(true);
  const [loading, setLoading] = useState(false);
  const [chip, setChip] = useState("");
  const [groupBy, setGroupBy] = useState("none"); // none | doc_type | patient
  const [range, setRange] = useState("7"); // 7 | 15 | 30 | 90 | all (days by upload date)
  const [openGroups, setOpenGroups] = useState({}); // group key → expanded (collapsed by default)
  const [page, setPage] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const inputRef = useRef(null);
  const dragRef = useRef(false);
  const [dragOver, setDragOver] = useState(false);

  // Load every analysis for the client (paged under the hood) so counts, grouping
  // and filtering can all run client-side.
  const load = useCallback(async () => {
    if (!selectedClientId) return;
    setLoading(true);
    try {
      const LIM = 200;
      let skip = 0, rows = [], total = Infinity;
      while (rows.length < total) {
        const d = await listAnalyses({ client_id: selectedClientId, skip, limit: LIM });
        total = d.total || 0;
        const batch = d.analyses || [];
        rows = rows.concat(batch);
        if (!batch.length) break;
        skip += LIM;
      }
      setAllRows(rows);
      if (ldFirst.current) { ldFirst.current = false; reportTraffic(rows.length); }
    } catch { toast.error("Could not load documents."); setAllRows([]); }
    finally { setLoading(false); }
  }, [selectedClientId, reportTraffic]);

  useEffect(() => { load(); }, [load]);
  // Switching client clears the list and any tracked jobs, then reloads.
  useEffect(() => { setJobs([]); setPage(0); setChip(""); setGroupBy("none"); setOpenId(null); }, [selectedClientId]);
  useEffect(() => { setPage(0); setOpenGroups({}); }, [chip, groupBy, range]);

  // Poll active jobs every 2s.
  useEffect(() => {
    if (!jobs.some((j) => j.status === "processing")) return;
    const t = setInterval(async () => {
      const active = jobs.filter((j) => j.status === "processing");
      let anyDone = false;
      await Promise.all(active.map(async (j) => {
        try {
          const d = await getJob(j.job_id);
          if (d.status === "completed") anyDone = true;
          setJobs((prev) => prev.map((x) => x.job_id === j.job_id ? {
            ...x,
            status: d.status,
            stage: d.progress?.stage || x.stage,
            analysis_id: d.analysis_id || x.analysis_id,
            doc_type: d.result?.classification?.document_type || x.doc_type,
            engine: d.result?.ai_engine || x.engine,
            match: d.edi_client_match || x.match,
            error: d.error || (d.status === "failed" ? "Processing failed" : x.error),
          } : x));
        } catch { /* keep trying */ }
      }));
      if (anyDone) load();
    }, 2000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const handleFiles = useCallback(async (fileList) => {
    if (!selectedClientId) { toast.error("Select a client first."); return; }
    const files = Array.from(fileList || []);
    const valid = [];
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) { toast.error(`${f.name} is over 10 MB`); continue; }
      if (!ACCEPTED.includes(f.type) && !/\.(pdf|png|jpe?g|webp|tiff?|heic)$/i.test(f.name)) { toast.error(`${f.name}: unsupported type`); continue; }
      valid.push(f);
    }
    if (!valid.length) return;
    // base64-encode all, then fire the uploads together (one call per file).
    const prepared = await Promise.all(valid.map(async (f) => ({ f, b64: await fileToBase64(f) })));
    await Promise.all(prepared.map(async ({ f, b64 }) => {
      try {
        const res = await uploadDocument({ client_id: selectedClientId, filename: f.name, content_type: f.type || "application/pdf", base64_data: b64 });
        setJobs((prev) => [{ job_id: res.job_id, filename: f.name, status: res.status || "processing", stage: "ocr" }, ...prev]);
      } catch (e) {
        toast.error(`${f.name}: upload failed`);
      }
    }));
  }, [selectedClientId]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false); dragRef.current = false;
    handleFiles(e.dataTransfer?.files);
  };

  const retry = (job) => setJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
  const dismissJob = (job) => setJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));

  // Upload-date window (by created_at). Default 7 days.
  const dated = useMemo(() => {
    if (range === "all") return allRows;
    const cutoff = Date.now() - Number(range) * 86400000;
    return allRows.filter((r) => { const t = new Date(r.created_at).getTime(); return isNaN(t) ? true : t >= cutoff; });
  }, [allRows, range]);

  // Counts per link status (for the filter chips), within the current date window.
  const counts = useMemo(() => {
    const c = { "": dated.length, auto_linked: 0, needs_review: 0, confirmed: 0 };
    dated.forEach((r) => { if (c[r.edi_link_status] != null) c[r.edi_link_status] += 1; });
    return c;
  }, [dated]);

  const filtered = useMemo(() => (chip ? dated.filter((r) => r.edi_link_status === chip) : dated), [dated, chip]);

  // Grouping: returns [{ key, label, rows }]. groupBy "none" → a single implicit group.
  const groups = useMemo(() => {
    if (groupBy === "none") return null;
    const keyOf = (r) => groupBy === "doc_type"
      ? (r.classification?.document_type || "unknown")
      : (r.edi_client_match?.patient?.full_name || "Unmatched");
    const map = new Map();
    filtered.forEach((r) => {
      const k = keyOf(r);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });
    return Array.from(map.entries())
      .map(([key, rows]) => ({ key, label: groupBy === "doc_type" ? prettyType(key) : key, rows }))
      .sort((a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label));
  }, [filtered, groupBy]);

  const total = filtered.length;
  const pageRows = useMemo(() => filtered.slice(page * PAGE, page * PAGE + PAGE), [filtered, page]);
  const pageInfo = useMemo(() => ({ from: total === 0 ? 0 : page * PAGE + 1, to: Math.min(page * PAGE + PAGE, total) }), [page, total]);

  if (!selectedClientId) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-24" data-testid="lumina-no-client">
        <FileText size={32} color={GREY} />
        <div className="text-[16px] font-bold mt-3" style={{ color: INK }}>Select a client to use Lumina Documents</div>
        <div className="text-[13px] mt-1" style={{ color: GREY }}>Every document session runs inside one client. Choose a client from the top bar to upload and review documents.</div>
      </div>
    );
  }

  return (
    <div data-testid="lumina-docs">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="u-page">Lumina Documents</h1>
          <div className="text-[13px] mt-0.5" style={{ color: SUB }}>Upload, classify and match documents for client <b style={{ color: INK }}>{selectedClientId}</b></div>
        </div>
        <div className="flex items-center gap-2">
          <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="lumina-range" className="ctl-select">
            <option value="7">Last 7 days</option>
            <option value="15">Last 15 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <button onClick={load} className="text-[12px] font-semibold inline-flex items-center gap-1.5 border rounded-[10px] px-3 bg-white hover:bg-black/[.02]" style={{ height: "var(--ctl-h)", borderColor: HAIR, color: SUB }} data-testid="lumina-refresh">
            <IconRefresh size={13} stroke={1.9} /> Refresh
          </button>
        </div>
      </div>

      {/* Upload dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (!dragRef.current) { dragRef.current = true; setDragOver(true); } }}
        onDragLeave={() => { dragRef.current = false; setDragOver(false); }}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="lumina-dropzone"
        className="cursor-pointer rounded-[14px] border-2 border-dashed flex flex-col items-center justify-center py-8 transition-colors"
        style={{ borderColor: dragOver ? INDIGO : "#D8D8DE", background: dragOver ? "#F3F3FD" : "#FBFBFD" }}
      >
        <ScanLine size={26} color={dragOver ? INDIGO : GREY} />
        <div className="text-[13.5px] font-semibold mt-2" style={{ color: INK }}>Drop files or click to upload</div>
        <div className="text-[11.5px] mt-0.5" style={{ color: GREY }}>PDF, images &amp; scans · up to 10 MB each · batch supported</div>
        <input ref={inputRef} type="file" multiple accept=".pdf,image/*" className="hidden" data-testid="lumina-file-input"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {/* Job tracker */}
      {jobs.length ? (
        <div className="mt-3 space-y-2" data-testid="lumina-jobs">
          {jobs.map((j) => (
            <div key={j.job_id} className="flex items-center gap-3 rounded-[10px] border px-3 py-2.5 bg-white" style={{ borderColor: HAIR }} data-testid={`lumina-job-${j.job_id}`}>
              <FileText size={15} color={GREY} />
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold truncate" style={{ color: INK }}>{j.filename}</div>
                <div className="mt-1"><StageTracker stage={j.stage} status={j.status} error={j.error} /></div>
              </div>
              {j.status === "completed" && j.analysis_id ? (
                <button onClick={() => setOpenId(j.analysis_id)} className="text-[11.5px] font-bold inline-flex items-center gap-1" style={{ color: INDIGO }} data-testid={`lumina-job-open-${j.job_id}`}>Open <ChevronRight size={13} /></button>
              ) : null}
              {j.status === "failed" ? (
                <button onClick={() => retry(j)} className="text-[11.5px] font-bold" style={{ color: "#C4302A" }} data-testid={`lumina-job-retry-${j.job_id}`}>Re-upload</button>
              ) : null}
              {j.status !== "processing" ? (
                <button onClick={() => dismissJob(j)} className="p-1 rounded-[6px] hover:bg-black/[.05]" data-testid={`lumina-job-dismiss-${j.job_id}`}><X size={14} color={GREY} /></button>
              ) : <Loader2 size={14} className="animate-spin" color={INDIGO} />}
            </div>
          ))}
        </div>
      ) : null}

      {/* Filters + grouping */}
      <div className="flex items-center gap-2 mt-6 mb-3 flex-wrap">
        {CHIPS.map((c) => (
          <button key={c.k || "all"} onClick={() => setChip(c.k)} data-testid={`lumina-chip-${c.k || "all"}`}
            className="text-[12px] font-semibold rounded-full px-3 py-1.5 border transition-colors inline-flex items-center gap-1.5"
            style={chip === c.k ? { background: INK, color: "#fff", borderColor: INK } : { background: "#fff", color: SUB, borderColor: HAIR }}>
            {c.t}
            <span className="text-[10.5px] font-bold rounded-full px-1.5 min-w-[18px] text-center" style={chip === c.k ? { background: "rgba(255,255,255,.22)", color: "#fff" } : { background: "#F0F0F3", color: GREY }} data-testid={`lumina-chip-count-${c.k || "all"}`}>
              {counts[c.k] ?? 0}
            </span>
          </button>
        ))}
        <span className="flex-1" />
        <span className="text-[11.5px] font-semibold" style={{ color: GREY }}>Group</span>
        <div className="inline-flex rounded-[9px] border overflow-hidden" style={{ borderColor: HAIR }} data-testid="lumina-groupby">
          {[["none", "None"], ["doc_type", "Document type"], ["patient", "Patient"]].map(([k, t], i) => (
            <button key={k} onClick={() => setGroupBy(k)} data-testid={`lumina-group-${k}`}
              className="text-[12px] font-semibold px-3 py-1.5 transition-colors"
              style={{ background: groupBy === k ? INK : "#fff", color: groupBy === k ? "#fff" : SUB, borderLeft: i ? `1px solid ${HAIR}` : "none" }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-[14px] border bg-white flex items-center justify-center py-16" style={{ borderColor: HAIR }}><Loader2 className="animate-spin" color={GREY} /></div>
      ) : total === 0 ? (
        <div className="rounded-[14px] border bg-white py-14 text-center" style={{ borderColor: HAIR }} data-testid="lumina-empty">
          <div className="text-[13px] font-semibold" style={{ color: INK }}>No documents{chip ? " in this status" : ""}{range !== "all" ? " in this window" : ""}</div>
          <div className="text-[12px] mt-1" style={{ color: GREY }}>{range !== "all" ? "Try a wider date range, or upload" : "Upload"} a file above to classify and match it to a patient.</div>
        </div>
      ) : groups ? (
        <div className="space-y-4" data-testid="lumina-grouped">
          {groups.map((g) => {
            const open = !!openGroups[g.key];
            return (
              <div key={g.key} className="rounded-[14px] border bg-white overflow-hidden" style={{ borderColor: HAIR }} data-testid={`lumina-group-block-${g.key}`}>
                <button onClick={() => setOpenGroups((p) => ({ ...p, [g.key]: !p[g.key] }))} data-testid={`lumina-group-toggle-${g.key}`}
                  className="w-full flex items-center gap-2 px-4 py-2.5 border-b hover:bg-black/[.015]" style={{ borderColor: open ? HAIR : "transparent", background: "#FBFBFD" }}>
                  {open ? <ChevronDown size={15} color={GREY} /> : <ChevronRight size={15} color={GREY} />}
                  {groupBy === "patient" ? <UserCheck size={14} color={GREY} /> : <FileText size={14} color={GREY} />}
                  <span className="text-[12.5px] font-bold" style={{ color: INK }}>{g.label}</span>
                  <span className="text-[10.5px] font-bold rounded-full px-1.5 min-w-[18px] text-center" style={{ background: "#F0F0F3", color: GREY }}>{g.rows.length}</span>
                </button>
                {open ? g.rows.map((row) => <DocRow key={row.id} row={row} onOpen={setOpenId} />) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[14px] border bg-white overflow-hidden" style={{ borderColor: HAIR }}>
          {pageRows.map((row) => <DocRow key={row.id} row={row} onOpen={setOpenId} />)}
        </div>
      )}

      {/* Pagination (flat view only) */}
      {!groups && total > PAGE ? (
        <div className="flex items-center justify-between mt-3">
          <span className="text-[12px]" style={{ color: GREY }} data-testid="lumina-page-info">{pageInfo.from}–{pageInfo.to} of {total}</span>
          <div className="flex items-center gap-2">
            <button disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))} className="text-[12px] font-semibold border rounded-[8px] px-3 py-1.5 bg-white disabled:opacity-40" style={{ borderColor: HAIR, color: SUB }} data-testid="lumina-prev">Previous</button>
            <button disabled={(page + 1) * PAGE >= total} onClick={() => setPage(page + 1)} className="text-[12px] font-semibold border rounded-[8px] px-3 py-1.5 bg-white disabled:opacity-40" style={{ borderColor: HAIR, color: SUB }} data-testid="lumina-next">Next</button>
          </div>
        </div>
      ) : null}

      {openId ? <DetailDrawer clientId={selectedClientId} analysisId={openId} onClose={() => setOpenId(null)} onChanged={load} /> : null}
    </div>
  );
}
